import { LabSegments } from 'utils/labSdk/labSegments'

/** One object the eraser can select with a click */
export interface LabObject {
  id: number
  concept: string
  /** [x0, y0, x1, y1], inclusive, in the map's work coordinates */
  bbox: [number, number, number, number]
  /** The object's own pixels over its box: white on transparent */
  mask: HTMLCanvasElement
}

/** The objects detected in the photo on stage, at the segments' work size (same aspect ratio as the photo) */
export interface LabObjectMap {
  w: number
  h: number
  /** Object id per work pixel, row by row (0 = none) */
  ids: Uint8Array
  objects: Map<number, LabObject>
}

/** The id of the object under a point of the photo (natural pixels), 0 for none */
export const labObjectIdAt = (map: Pick<LabObjectMap, 'w' | 'h' | 'ids'>, x: number, y: number, naturalWidth: number, naturalHeight: number): number => {
  if (!naturalWidth || !naturalHeight || x < 0 || y < 0 || x >= naturalWidth || y >= naturalHeight) return 0
  const wx = Math.min(map.w - 1, Math.floor((x * map.w) / naturalWidth))
  const wy = Math.min(map.h - 1, Math.floor((y * map.h) / naturalHeight))
  return map.ids[wy * map.w + wx] ?? 0
}

/** Where an object's mask lands on the photo, in natural pixels */
export const labObjectRect = (object: Pick<LabObject, 'bbox'>, map: Pick<LabObjectMap, 'w' | 'h'>, naturalWidth: number, naturalHeight: number) => {
  const scaleX = naturalWidth / map.w
  const scaleY = naturalHeight / map.h
  const [x0, y0, x1, y1] = object.bbox
  return { x: x0 * scaleX, y: y0 * scaleY, width: (x1 - x0 + 1) * scaleX, height: (y1 - y0 + 1) * scaleY }
}

/** The label map's red channel is the object id (it is a grey PNG) */
export const labelPixelsToIds = (pixels: Uint8ClampedArray): Uint8Array => {
  const ids = new Uint8Array(pixels.length / 4)
  for (let index = 0; index < ids.length; index += 1) ids[index] = pixels[index * 4]
  return ids
}

/**
 * An object crop is opaque white-on-black; drawn as is it would paint its whole box. Its luminance becomes the alpha
 * of a white mask, in place.
 */
export const objectPixelsToMask = (pixels: Uint8ClampedArray): void => {
  for (let index = 0; index < pixels.length; index += 4) {
    pixels[index + 3] = pixels[index]
    pixels[index] = 255
    pixels[index + 1] = 255
    pixels[index + 2] = 255
  }
}

const loadPng = (base64: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const image = new Image()
  image.onload = () => resolve(image)
  image.onerror = () => reject(new Error('Lab segments: undecodable PNG'))
  image.src = `data:image/png;base64,${base64}`
})

const toCanvas = (image: HTMLImageElement, width: number, height: number) => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) throw new Error('Lab segments: no 2D canvas')
  context.drawImage(image, 0, 0, width, height)
  return { canvas, context }
}

/** Decodes the segments' PNGs into the id map and one mask per object */
export const decodeLabObjects = async (segments: LabSegments): Promise<LabObjectMap> => {
  const labels = toCanvas(await loadPng(segments.labelsPng), segments.w, segments.h)
  const ids = labelPixelsToIds(labels.context.getImageData(0, 0, segments.w, segments.h).data)

  const objects = new Map<number, LabObject>()
  await Promise.all(segments.instances.map(async (instance) => {
    const [x0, y0, x1, y1] = instance.bbox
    const width = Math.max(1, x1 - x0 + 1)
    const height = Math.max(1, y1 - y0 + 1)
    const crop = toCanvas(await loadPng(instance.png), width, height)
    const imageData = crop.context.getImageData(0, 0, width, height)
    objectPixelsToMask(imageData.data)
    crop.context.putImageData(imageData, 0, 0)
    objects.set(instance.id, { id: instance.id, concept: instance.concept, bbox: instance.bbox, mask: crop.canvas })
  }))

  return { w: segments.w, h: segments.h, ids, objects }
}
